import { confirmPaymentIntent } from "@circle-react/components/Paywalls/Checkout/Attempts/helpers/stripe";
import { useVerifyAttempt } from "@circle-react/components/Paywalls/Checkout/Attempts/hooks/useVerifyAttempt";
import { stages } from "@circle-react/components/Paywalls/Checkout/Attempts/types";
import type { OnStageChangeProps } from "@circle-react/components/Paywalls/Checkout/Attempts/types";
import { CheckoutError } from "@circle-react/components/Paywalls/Checkout/CheckoutError";
import { useCheckoutSession } from "@circle-react/components/Paywalls/Checkout/contexts/CheckoutSessionContext";
import { constructReturnUrl } from "@circle-react/components/Paywalls/Checkout/helpers";
import { firePurchaseForCompletedSteps } from "@circle-react/components/Paywalls/CheckoutWithSession/helpers/ga4PurchaseHelper";
import { useGoToNextStep } from "@circle-react/components/Paywalls/CheckoutWithSession/hooks/useGoToNextStep";
import { paywallCheckout } from "@circle-react/helpers/urlHelpers/internalApi/paywalls/checkout";
import { loadStripeWrapper } from "@circle-react/hooks/paywalls/useStripeElement";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useMutation } from "react-query";

import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";

import {
  buildUpgradeAcceptanceBody,
  getUpgradePaymentFailureMessage,
  getUpgradeUnresolvedMessage,
} from "./helpers";
import type {
  UpgradeAcceptanceBody,
  UpgradeAcceptanceResponse,
  UseUpgradeAcceptanceProps,
} from "./types";
import { useUpgradeAcceptanceError } from "./useUpgradeAcceptanceError";
import { useWaitForUpgradeStepCompletion } from "./useWaitForUpgradeStepCompletion";

export const useUpgradeAcceptance = ({
  checkoutPaywallId,
  checkoutSessionId,
  couponMode,
  hasPreview,
  previewCouponId,
  previewPriceId,
  invoiceId,
  onAcceptanceStateChange,
  onCouponRefreshRequired,
  paymentIntentId,
  readOnlyProcessing = false,
  selectedPaywallPriceId,
  setupIntentId,
  stepId,
}: UseUpgradeAcceptanceProps) => {
  const { clearErrors, setError } = useFormContext();
  const { ga4FiredStepIdsRef, refetchSession, session } = useCheckoutSession();
  const { goToNextStep } = useGoToNextStep();
  const [isAccepted, setIsAccepted] = useState(
    readOnlyProcessing ||
      Boolean(paymentIntentId || invoiceId || setupIntentId),
  );
  const [isProcessing, setIsProcessing] = useState(readOnlyProcessing);
  const [targetTrialDays, setTargetTrialDays] = useState<number>();
  const { isMountedRef, waitForStepCompletion } =
    useWaitForUpgradeStepCompletion({ goToNextStep, refetchSession, stepId });
  const hasStartedStatusRead = useRef(false);
  const hasStartedReturnVerification = useRef(false);

  const handleAcceptanceError = useUpgradeAcceptanceError({
    isMountedRef,
    onAcceptanceStateChange,
    readOnlyProcessing,
    setIsAccepted,
    setIsProcessing,
  });

  const handleVerificationStageChange = async (result: OnStageChangeProps) => {
    if (!isMountedRef.current) return;
    if (result.stage === stages.failed) {
      handleAcceptanceError(result.error.message);
      return;
    }
    if (result.stage !== stages.completed) return;
    setTargetTrialDays(result.targetTrialDays);

    try {
      let currentSession = session;
      try {
        currentSession = await refetchSession();
      } catch {
        // Keep checkout navigation best-effort, as in the Add-on flow.
      }
      if (!isMountedRef.current) return;
      if (currentSession) {
        firePurchaseForCompletedSteps(currentSession, ga4FiredStepIdsRef);
      }
      goToNextStep(currentSession, stepId);
    } finally {
      if (isMountedRef.current) setIsProcessing(false);
    }
  };

  const verifyAttempt = useVerifyAttempt({
    canVerifyAttempt: () => true,
    onStageChange: result => {
      void handleVerificationStageChange(result);
    },
    paywallId: checkoutPaywallId,
    sessionId: checkoutSessionId,
  });

  useEffect(() => {
    if (
      readOnlyProcessing ||
      hasStartedReturnVerification.current ||
      (!paymentIntentId && !invoiceId && !setupIntentId)
    ) {
      return;
    }
    hasStartedReturnVerification.current = true;
    setIsAccepted(true);
    setIsProcessing(true);
    if (
      [paymentIntentId, invoiceId, setupIntentId].filter(Boolean).length > 1
    ) {
      handleAcceptanceError(getUpgradeUnresolvedMessage());
    } else if (paymentIntentId?.startsWith("pi_")) {
      void verifyAttempt({ paymentIntentId });
    } else if (invoiceId?.startsWith("in_")) {
      void verifyAttempt({ invoiceId });
    } else if (setupIntentId?.startsWith("seti_")) {
      void verifyAttempt({ setupIntentId });
    } else {
      handleAcceptanceError(getUpgradeUnresolvedMessage());
    }
  }, [
    handleAcceptanceError,
    invoiceId,
    paymentIntentId,
    readOnlyProcessing,
    setupIntentId,
    verifyAttempt,
  ]);

  useEffect(() => {
    if (!readOnlyProcessing || hasStartedStatusRead.current) return;
    hasStartedStatusRead.current = true;
    void waitForStepCompletion()
      .then(completed => {
        if (isMountedRef.current && !completed) {
          setError("root", { message: getUpgradeUnresolvedMessage() });
        }
      })
      .catch(() => {
        if (isMountedRef.current) {
          setError("root", { message: getUpgradeUnresolvedMessage() });
        }
      })
      .finally(() => {
        if (isMountedRef.current) setIsProcessing(false);
      });
  }, [waitForStepCompletion, readOnlyProcessing, setError]);
  const showUnresolved = () => {
    handleAcceptanceError(getUpgradeUnresolvedMessage());
  };
  const handlePaymentIntent = async (
    transactionId: string,
    status: string | undefined,
    clientSecret: string | undefined,
  ) => {
    if (status === "requires_action" && clientSecret) {
      try {
        if (!session)
          throw CheckoutError.generic("UpgradeOfferPage.acceptUpgrade");
        const stripe = await loadStripeWrapper({
          stripeAccountId: session.community.payment_processor.processor_id,
          stripePublicKey: session.community.payment_processor.processor_key,
          locale: window.locale,
        });
        if (!stripe)
          throw CheckoutError.generic("UpgradeOfferPage.acceptUpgrade");
        await confirmPaymentIntent({
          caller: "UpgradeOfferPage.acceptUpgrade",
          confirmParams: {
            return_url: constructReturnUrl({
              paymentIntentId: transactionId,
              paymentMethodType: "",
              sessionId: checkoutSessionId,
            }),
          },
          paymentIntentClientSecret: clientSecret,
          stripe,
        });
      } catch (error: unknown) {
        if (!isMountedRef.current) return;
        handleAcceptanceError(
          CheckoutError.fromUnknownError(
            "UpgradeOfferPage.acceptUpgrade",
            error,
          ).message,
        );
        return;
      }
    } else if (status !== "processing" && status !== "succeeded") {
      showUnresolved();
      return;
    }
    await verifyAttempt({ paymentIntentId: transactionId });
  };
  const handleNoIdentity = async () => {
    try {
      if (!(await waitForStepCompletion())) showUnresolved();
    } catch {
      showUnresolved();
    } finally {
      if (isMountedRef.current) setIsProcessing(false);
    }
  };
  const acceptMutation = useMutation({
    mutationFn: () =>
      reactQueryPost<UpgradeAcceptanceResponse, UpgradeAcceptanceBody>(
        paywallCheckout.sessionStep.acceptUpgrade({
          paywallId: checkoutPaywallId,
          stepId,
        }),
        buildUpgradeAcceptanceBody(
          selectedPaywallPriceId,
          couponMode,
          previewCouponId,
        ),
      ),
    onSuccess: async response => {
      if (!isMountedRef.current) return;
      if (response.outcome === "coupon_refresh_required") {
        setIsAccepted(false);
        setIsProcessing(false);
        onAcceptanceStateChange(false);
        onCouponRefreshRequired();
        return;
      }

      setTargetTrialDays(response.target_trial_days);
      if (response.payment_failure_code) {
        handleAcceptanceError(
          getUpgradePaymentFailureMessage(
            response.change_deferred_until_payment,
          ),
        );
        return;
      }

      const transactionId = response.processor_transaction_id;
      const status = response.payment_intent_status;
      if (transactionId?.startsWith("pi_")) {
        await handlePaymentIntent(
          transactionId,
          status,
          response.payment_intent_client_secret,
        );
        return;
      }
      if (
        transactionId?.startsWith("in_") &&
        !response.payment_intent_client_secret &&
        (!status || status === "succeeded")
      ) {
        await verifyAttempt({ invoiceId: transactionId });
        return;
      }
      if (
        !transactionId &&
        !response.payment_intent_client_secret &&
        (!status || status === "succeeded")
      ) {
        await handleNoIdentity();
        return;
      }
      showUnresolved();
    },
    onError: (error: Error) => {
      handleAcceptanceError(error.message);
    },
  });

  const accept = () => {
    if (
      readOnlyProcessing ||
      isAccepted ||
      !selectedPaywallPriceId ||
      !hasPreview ||
      previewPriceId !== selectedPaywallPriceId
    ) {
      return;
    }
    clearErrors();
    setIsAccepted(true);
    setIsProcessing(true);
    onAcceptanceStateChange(true);
    acceptMutation.mutate();
  };
  return {
    accept,
    isAccepted,
    isAccepting: acceptMutation.isPending || isProcessing,
    isProcessing,
    targetTrialDays,
  };
};
